const API_BASE = '/api';

async function request(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

export async function register(email, password) {
  return request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function login(email, password) {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function logout() {
  return request('/auth/logout', { method: 'POST' });
}

export async function getMe() {
  return request('/auth/me');
}

export async function searchClauses(query, topK = 10, jurisdiction = null) {
  const body = { query, top_k: topK };
  if (jurisdiction) body.jurisdiction = jurisdiction;
  return request('/search', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function benchmarkClause(clauseText) {
  return request('/benchmark', {
    method: 'POST',
    body: JSON.stringify({ clause_text: clauseText }),
  });
}

export async function getClause(clauseId) {
  return request(`/search/clause/${clauseId}`);
}

export async function listFiles() {
  return request('/files');
}

export async function uploadFile(file, password) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('password', password);
  const res = await fetch(`${API_BASE}/files/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
}

export async function deleteFile(fileId) {
  return request(`/files/${fileId}`, { method: 'DELETE' });
}

export async function createCheckout(plan = 'monthly') {
  return request('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });
}

export async function createCheckoutReport() {
  return request('/billing/checkout-report', { method: 'POST' });
}

export async function openBillingPortal() {
  return request('/billing/portal', { method: 'POST' });
}

export async function getArbitrationComparison() {
  return request('/arbitration-comparison');
}

export async function contractScorecard(contractText) {
  return request('/scorecard', {
    method: 'POST',
    body: JSON.stringify({ contract_text: contractText }),
  });
}

export async function downloadBenchmarkReport(clauseText) {
  const res = await fetch(`${API_BASE}/report/benchmark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ clause_text: clauseText }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Report generation failed');
  }
  return res.blob();
}

export async function downloadScorecardReport(contractText) {
  const res = await fetch(`${API_BASE}/report/scorecard`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ contract_text: contractText }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Report generation failed');
  }
  return res.blob();
}

export async function getSearchHistory(limit = 50) {
  return request(`/search-history?limit=${limit}`);
}

// --- IP Cross-Reference ---

export async function ipSearch(query) {
  return request('/ip/search', {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
}

export async function ipCrossReference(clauseText) {
  return request('/ip/cross-reference', {
    method: 'POST',
    body: JSON.stringify({ clause_text: clauseText }),
  });
}

// --- Deal Structure Templates ---

export async function listTemplates() {
  return request('/templates');
}

export async function getTemplate(templateId) {
  return request(`/templates/${templateId}`);
}

// --- Clause Version Tracking ---

export async function clauseEvolution(query) {
  return request('/evolution', {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
}

// --- Clause Alerts ---

export async function createAlert(alertData) {
  return request('/alerts', { method: 'POST', body: JSON.stringify(alertData) });
}

export async function listAlerts() {
  return request('/alerts');
}

export async function updateAlert(alertId, updates) {
  return request(`/alerts/${alertId}`, { method: 'PATCH', body: JSON.stringify(updates) });
}

export async function deleteAlert(alertId) {
  return request(`/alerts/${alertId}`, { method: 'DELETE' });
}

export async function checkAlert(alertId) {
  return request(`/alerts/${alertId}/check`, { method: 'POST' });
}

export async function getAlertMatches(alertId) {
  return request(`/alerts/${alertId}/matches`);
}

// --- Multi-Jurisdiction Comparison ---

export async function jurisdictionCompare(query) {
  return request('/jurisdiction-compare', {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
}

// --- Saved Clause Library ---

export async function saveClause(clauseData) {
  return request('/library', {
    method: 'POST',
    body: JSON.stringify(clauseData),
  });
}

export async function listSavedClauses(folder = null) {
  const qs = folder ? `?folder=${encodeURIComponent(folder)}` : '';
  return request(`/library${qs}`);
}

export async function updateSavedClause(savedId, updates) {
  return request(`/library/${savedId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteSavedClause(savedId) {
  return request(`/library/${savedId}`, { method: 'DELETE' });
}

export async function joinMaxWaitlist(name, email) {
  return request('/waitlist/max', {
    method: 'POST',
    body: JSON.stringify({ name, email }),
  });
}
